import { ApiError } from '@eneco/dc/client/ApiError';
import { getCustomerAccountSummary, putDiscontinueConfirmation } from '@eneco/dc/services';
import {
  DC_Domain_Models_Products_ProductDiscontinueReason,
  Products_Relocations_DiscontinueProductType,
  ResponseDataSystem_String,
  ResponseModels_CustomerAccountSummary_CustomerAccountSummary,
} from '@monorepo-types/dc';
import { assign, fromPromise, setup } from 'xstate';

import { MachineConfig } from '../types';

export interface CancellationOverviewFetchValues {
  accountId: number | null;
  customerId: number | null;
}

interface ProductCancellationSubmitValues extends CancellationOverviewFetchValues {
  reason?: DC_Domain_Models_Products_ProductDiscontinueReason;
  explanation?: string;
}

export type CancellationOverviewSubmitHandler<TSubmitValues extends CancellationOverviewFetchValues> = (
  config: MachineConfig,
  values: TSubmitValues,
) => Promise<unknown>;

const putDiscontinue = (
  productType: Products_Relocations_DiscontinueProductType,
  config: MachineConfig,
  reasonInformation: ProductCancellationSubmitValues,
): Promise<{ data: ResponseDataSystem_String }> => {
  const requestBody = {
    data: {
      date: new Date().toISOString(),
      reason: reasonInformation.reason as DC_Domain_Models_Products_ProductDiscontinueReason,
      explanation: reasonInformation.explanation,
    },
  };

  return new Promise((resolve, reject) =>
    putDiscontinueConfirmation({
      businessUnit: config.businessUnit,
      label: config.label,
      customerId: Number(reasonInformation.customerId),
      accountId: Number(reasonInformation.accountId),
      productType,
      requestBody,
    })
      .then(response => {
        resolve({ data: response });
      })
      .catch((error: ApiError) => {
        reject(new Error(error?.message));
      }),
  );
};

export const createCancellationOverviewMachineWithSubmit = <
  TSubmitValues extends CancellationOverviewFetchValues,
>(
  submit: CancellationOverviewSubmitHandler<TSubmitValues>,
) => {
  type OverviewEvent =
    | {
        type: 'FETCH_DATA';
        config: MachineConfig;
        values: CancellationOverviewFetchValues;
      }
    | {
        type: 'SUBMIT';
        config: MachineConfig;
        values: TSubmitValues;
      }
    | { type: 'RETRY_FETCH' }
    | { type: 'RETRY_SUBMIT' }
    | { type: 'RESET' };

  type FetchEvent = Extract<OverviewEvent, { type: 'FETCH_DATA' }>;
  type SubmitEvent = Extract<OverviewEvent, { type: 'SUBMIT' }>;

  interface OverviewMachineContext {
    errorMessage?: string;
    summary?: ResponseModels_CustomerAccountSummary_CustomerAccountSummary;
    lastFetchEvent?: FetchEvent;
    lastSubmitEvent?: SubmitEvent;
  }

  interface InputTypeFetch {
    event?: FetchEvent;
  }

  interface InputTypeSubmit {
    event?: SubmitEvent;
  }

  return setup({
    types: {} as {
      context: OverviewMachineContext;
      events: OverviewEvent;
      actors: {
        fetchData: {
          input: InputTypeFetch;
          output: ResponseModels_CustomerAccountSummary_CustomerAccountSummary;
        };
        submitData: {
          input: InputTypeSubmit;
          output: unknown;
        };
      };
    },
    actors: {
      fetchData: fromPromise<ResponseModels_CustomerAccountSummary_CustomerAccountSummary, InputTypeFetch>(
        ({ input }) => {
          const { event } = input;

          if (event?.type !== 'FETCH_DATA') {
            return Promise.reject(new Error('Invalid event type for fetchData actor'));
          }

          return getCustomerAccountSummary({
            businessUnit: event.config.businessUnit,
            label: event.config.label,
            customerId: Number(event.values.customerId),
            accountId: Number(event.values.accountId),
          });
        },
      ),
      submitData: fromPromise<unknown, InputTypeSubmit>(({ input }) => {
        const { event } = input;

        if (event?.type !== 'SUBMIT') {
          return Promise.reject(new Error('Invalid event type for submitData actor'));
        }

        return submit(event.config, event.values);
      }),
    },
  }).createMachine({
    id: 'overview-machine',
    initial: 'IDLE',
    context: {
      summary: undefined,
      errorMessage: undefined,
      lastFetchEvent: undefined,
      lastSubmitEvent: undefined,
    },
    states: {
      IDLE: {
        on: {
          FETCH_DATA: {
            target: 'FETCHING',
            actions: assign({
              lastFetchEvent: ({ event }) => event,
            }),
          },
          SUBMIT: {
            target: 'SUBMITTING',
            actions: assign({
              lastSubmitEvent: ({ event }) => event,
            }),
          },
        },
      },
      FETCHING: {
        invoke: {
          src: 'fetchData',
          input: ({ event, context }) => ({
            event: event.type === 'FETCH_DATA' ? event : context.lastFetchEvent,
          }),
          onDone: {
            target: 'IDLE',
            actions: assign({
              summary: ({ event }) => event.output,
            }),
          },
          onError: {
            target: 'ERROR',
            actions: assign({
              errorMessage: ({ event }) => (event.error as Error).message,
            }),
          },
        },
      },
      SUBMITTING: {
        invoke: {
          src: 'submitData',
          input: ({ event, context }) => ({
            event: event.type === 'SUBMIT' ? event : context.lastSubmitEvent,
          }),
          onDone: {
            target: 'SUCCESS',
          },
          onError: {
            target: 'ERROR',
            actions: assign({
              errorMessage: ({ event }) => (event.error as Error).message,
            }),
          },
        },
      },
      SUCCESS: {
        type: 'final',
      },
      ERROR: {
        on: {
          RETRY_FETCH: { target: 'FETCHING' },
          RETRY_SUBMIT: { target: 'SUBMITTING' },
          RESET: { target: 'IDLE' },
        },
      },
    },
  });
};

export const createCancellationOverviewMachine = (
  productType: Products_Relocations_DiscontinueProductType,
) =>
  createCancellationOverviewMachineWithSubmit<ProductCancellationSubmitValues>((config, values) =>
    putDiscontinue(productType, config, values),
  );
